import React, { useEffect, useState, useRef } from "react";
import { MapInteractionCSS } from "react-map-interaction";
import style from "./canvas.module.scss";
import Canvas from "./Canvas";
import { useSelector } from "react-redux";
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import {useWindowSize} from '../../../helpers/hooks/useWindowSize'
import {
  isInvalidIOSSize,
  calculateIOSCanvasSize
} from '../../../helpers/Canvas/size'

const MANUAL_ZOOM_STEPS = 20

const translationY = () =>
  window.innerWidth <= 768 ? 150 : 0

const ZoomedImg = ({img}) => {
  const HEADER_HEIGHT = 150;
  const SIDE_MARGIN = 50;
  const [value, setValue] = useState({scale: 1, translation: {x: 0, y: translationY()}})
  const [scale, setScale] = useState(null)
  const [maxScale, setMaxScale] = useState(2)
  const windowSize = useWindowSize()
  const zoomed = useSelector (state => state.instruments.zoomed)
  const map = useSelector (state => state.map.map)
  const [mapSize, setMapSize] = useState({ width: 0, height: 0 })

  const mapInteractionContainerRef = useRef(null)


  const scaleImage = value => zoomed && value && setValue(value)

  const getScaleDiscrete = () => Math.abs(maxScale - scale) / MANUAL_ZOOM_STEPS

  useEffect(() => {
    if (map) {
      setMapSize(
        isInvalidIOSSize(map.width, map.height)
          ? calculateIOSCanvasSize(map.width, map.height)
          : { width: map.width, height: map.height }
      )
    }
  }, [map, windowSize])

  useEffect(()=>{
    if (windowSize && mapSize) {
      const scale = calculateScale(mapSize, windowSize)
      const minScale = calculateMinScale(mapSize, windowSize)

      setScale(minScale)
      setValue(() => {
        return {
          scale: scale,
          translation: {
            x: windowSize.width / 2 - mapSize.width * scale / 2 ,
            // y: windowSize.height / 2 - map.height * scale / 2
            y: translationY()
          }}
      })
    }
  }, [mapSize, windowSize])

  const calculateScale = (img, window) => {
    return window.width ? window.width/img.width : 1
  }

  const calculateMinScale = (img, window) => {
    const imageWidth =  img.width
    const imageHeight =  img.height
    const scale1 =  imageWidth > window.width
      ? (window.width - SIDE_MARGIN) / imageWidth : 1
    const scale2 =  imageHeight > window.height
      ? (window.height - HEADER_HEIGHT) / imageHeight : 1

    return Math.min(scale1, scale2)
  }

  const scaleFromPoint = (newScale, focalPt) => {
    const { translation, scale } = value
    const scaleRatio = newScale / (scale !== 0 ? scale : 1)

    const focalPtDelta = {
      x: (scaleRatio * focalPt.x) - focalPt.x,
      y: (scaleRatio * focalPt.y) - focalPt.y
    }

    const newTranslation = {
      x: translation.x - focalPtDelta.x,
      y: translation.y - focalPtDelta.y
    }

    setValue(
      {
        scale: newScale,
        translation: newTranslation
      }
    )
  }

  const clientPosToTranslatedPos = ({ x, y }) => {
    const clientOffset = mapInteractionContainerRef.current.getBoundingClientRect()
    const origin = {
      x: clientOffset.left + value.translation.x,
      y: clientOffset.top + value.translation.y,
    }

    return { x: x - origin.x, y: y - origin.y }
  }

  const handleManualZoom = (direction = 1) => () => {
    if ((value.scale > scale || direction >= 0) && (value.scale < maxScale || direction < 0)) {
      const discrete = getScaleDiscrete()
      const currentDelta = value.scale - scale
      const parts = currentDelta/discrete
      let currStep = direction < 0 ? Math.floor(parts) : Math.ceil(parts)

      if (direction < 0) {
        if (currStep > 0) {
          --currStep
        } else {
          currStep = 0
        }
      } else {
        ++currStep
      }

      const newScale = !currStep ? scale : currStep*discrete+scale
      const centerPos = clientPosToTranslatedPos({ x: windowSize.width/2, y: windowSize.height/2 })

      scaleFromPoint(newScale, centerPos)
    }
  }

  return map && scale ?
    (<div className={style.WrappedImg} ref={mapInteractionContainerRef}>

      <MapInteractionCSS
        className={style.MapInteractionCSS}
        value={value}
        onChange={value => scaleImage(value)}
        minScale={scale}
        maxScale={maxScale}
        //translationBounds = {calculateTranslationBounds(scale)}
      >
        <Canvas img={img} map = {map}/>
      </MapInteractionCSS>

      <div className={style.ZoomControls}>
        <div
          className={`${style.ZoomControlsControl} ${value.scale >= maxScale ? style.ZoomControlsControl_disabled : ''}`}
          onClick={handleManualZoom()}
        >
          <ZoomInIcon />
        </div>
        <div
          className={`${style.ZoomControlsControl} ${value.scale <= scale ? style.ZoomControlsControl_disabled : ''}`}
          onClick={handleManualZoom(-1)}
        >
          <ZoomOutIcon />
        </div>
      </div>
    </div>)
  : null
}

export default ZoomedImg;
